/**
 * Processing-progress stepper rendered while a document is parsing or
 * being autofilled. Pure presentational component — the parent owns the
 * polling that keeps `doc` fresh (see `useDocument` in `api/queries.ts`).
 */

import { AlertTriangle, Check, Loader2, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  AUTOFILL_STAGES,
  friendlyErrorMessage,
  PROCESSING_STAGES,
  PROCESSING_STAGES_WITH_AUTOFILL,
  type DocumentDetail,
  type JobRead,
  type ProgressStage,
  type ProgressStageStep,
} from '@/api/docfiller-types'
import { cn } from '@/lib/utils'

interface ProcessingProgressProps {
  doc: DocumentDetail
  /**
   * Most-recent background job row for this doc (newest-first from the
   * /jobs API). When present the error card can show ``Retrying in 1m
   * (attempt 2 of 3)`` instead of a flat retry button, since the worker
   * will automatically re-run failed jobs on a backoff before exhausting
   * the budget.
   */
  latestJob?: JobRead | null
  onRetry?: () => void
  isRetrying?: boolean
}

export function ProcessingProgress({
  doc,
  latestJob,
  onRetry,
  isRetrying,
}: ProcessingProgressProps) {
  const isError = doc.status === 'error'
  const isUploadFilling =
    doc.status === 'filling' || doc.progress_stage === 'autofilling'
  const isFilling = isUploadFilling || doc.status === 'filled'
  const stages = isUploadFilling
    ? PROCESSING_STAGES_WITH_AUTOFILL
    : doc.status === 'filled'
      ? AUTOFILL_STAGES
      : PROCESSING_STAGES

  const activeIndex = activeStageIndex(stages, doc.progress_stage)

  if (isError) {
    return (
      <ProcessingErrorCard
        message={friendlyErrorMessage(doc)}
        canRetry={Boolean(onRetry && doc.is_retryable)}
        onRetry={onRetry}
        isRetrying={Boolean(isRetrying)}
        latestJob={latestJob ?? null}
      />
    )
  }

  const percent =
    typeof doc.progress_percent === 'number' ? doc.progress_percent : 0

  return (
    <Card
      className="space-y-3 p-4"
      data-testid="processing-progress"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <Loader2 className="size-4 animate-spin text-primary" aria-hidden />
        <span className="text-sm font-semibold tracking-tight">
          {isFilling ? 'Autofilling document' : 'Processing your document'}
        </span>
        {typeof doc.progress_percent === 'number' && (
          <span
            className="ml-auto text-xs font-medium tabular-nums text-muted-foreground"
            data-testid="processing-progress-percent"
          >
            {doc.progress_percent}%
          </span>
        )}
      </div>

      <Progress
        percent={percent}
        size="sm"
        tone={isFilling ? 'success' : 'primary'}
        data-testid="processing-progress-bar"
      />

      <ol className="space-y-1" data-testid="processing-progress-steps">
        {stages.map((step, i) => (
          <StepRow
            key={step.stage}
            step={step}
            state={
              i < activeIndex
                ? 'done'
                : i === activeIndex
                  ? 'active'
                  : 'pending'
            }
          />
        ))}
      </ol>
    </Card>
  )
}

function ProcessingErrorCard({
  message,
  canRetry,
  onRetry,
  isRetrying,
  latestJob,
}: {
  message: string
  canRetry: boolean
  onRetry?: () => void
  isRetrying: boolean
  latestJob: JobRead | null
}) {
  // ``is_retrying`` on the job means the worker has scheduled another
  // attempt — show a countdown badge instead of nudging the user toward
  // the manual Retry path. The latest job exposes ``retries`` (attempts
  // so far) and ``max_retries`` (budget) so we can render "attempt N of
  // M" without the user having to count the job-history list.
  const autoRetrying = Boolean(latestJob?.is_retrying)
  return (
    <Card
      className="space-y-3 border-destructive/40 bg-destructive-soft/60 p-4"
      data-testid="processing-error"
      role="alert"
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive"
        >
          <AlertTriangle className="size-3.5" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-destructive-soft-foreground">
            Processing failed
          </p>
          <p className="text-sm leading-relaxed text-destructive-soft-foreground/90">
            {message}
          </p>
          {latestJob && (
            <JobAttemptLine job={latestJob} autoRetrying={autoRetrying} />
          )}
        </div>
      </div>
      {canRetry && !autoRetrying && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onRetry}
            disabled={isRetrying}
            data-testid="processing-retry"
          >
            {isRetrying ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <RotateCw aria-hidden />
            )}
            {isRetrying ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      )}
    </Card>
  )
}

function JobAttemptLine({
  job,
  autoRetrying,
}: {
  job: JobRead
  autoRetrying: boolean
}) {
  // ``retries`` is the count of *completed* failed attempts. The next
  // attempt is therefore ``retries + 1``. When the worker has already
  // burned the budget, ``is_retrying`` is false and we say so out loud so
  // the user knows clicking Retry kicks a fresh chain, not just nudges
  // the worker.
  const attempt = Math.min(job.retries + 1, job.max_retries)
  if (autoRetrying && job.retry_after) {
    const eta = formatRetryEta(job.retry_after)
    return (
      <p
        className="text-xs text-destructive-soft-foreground/80"
        data-testid="processing-retry-countdown"
      >
        Retrying {eta} · attempt {attempt} of {job.max_retries}
      </p>
    )
  }
  if (job.is_terminal && job.retries >= job.max_retries) {
    return (
      <p
        className="text-xs text-destructive-soft-foreground/80"
        data-testid="processing-retry-exhausted"
      >
        Tried {job.max_retries} times — Retry to start a fresh attempt.
      </p>
    )
  }
  return null
}

function formatRetryEta(retryAfter: string): string {
  const target = new Date(retryAfter).getTime()
  const now = Date.now()
  const seconds = Math.max(0, Math.round((target - now) / 1000))
  if (seconds <= 1) return 'now'
  if (seconds < 60) return `in ${seconds}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.round(minutes / 60)
  return `in ${hours} hour${hours === 1 ? '' : 's'}`
}

type StepState = 'pending' | 'active' | 'done'

function StepRow({
  step,
  state,
}: {
  step: ProgressStageStep
  state: StepState
}) {
  return (
    <li
      className={cn(
        'flex items-start gap-2.5 rounded-md px-2 py-1.5 text-xs transition-colors',
        state === 'active' && 'bg-primary-soft/60',
      )}
      data-stage={step.stage}
      data-state={state}
    >
      <StepIcon state={state} />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'font-medium tracking-tight',
            state === 'pending' && 'text-muted-foreground',
            state === 'done' && 'text-foreground/80',
            state === 'active' && 'text-foreground',
          )}
        >
          {step.label}
        </div>
        <div className="text-muted-foreground">{step.description}</div>
      </div>
    </li>
  )
}

function StepIcon({ state }: { state: StepState }) {
  if (state === 'done') {
    return (
      <span
        className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground"
        aria-hidden
      >
        <Check className="size-3" />
      </span>
    )
  }
  if (state === 'active') {
    return (
      <span
        className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center"
        aria-hidden
      >
        <Loader2 className="size-3.5 animate-spin text-primary" />
      </span>
    )
  }
  return (
    <span
      className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border-strong"
      aria-hidden
    />
  )
}

function activeStageIndex(
  stages: readonly ProgressStageStep[],
  stage: ProgressStage | null | undefined,
): number {
  if (!stage) return 0
  const idx = stages.findIndex((s) => s.stage === stage)
  return idx === -1 ? 0 : idx
}
